import { Container, Graphics, InteractionEvent, LINE_CAP, LINE_JOIN, Text } from "pixi.js"
import { SpriteLabEntry, SpriteLabJob, SpriteLabMakeKind, SpriteLabRequest, SpriteLabState } from "game-common/spritelab"
import { chamfer, FACE, GOLD, groove, INK, MUTED, PLOT_FACE, PLOT_FACE_ON, PLOT_RIM, RIM, RIM_LIGHT, TIMBER, TIMBER_EDGE } from "./build_board"
import { drawWheelIcon, WHEEL_ICON_BOX, WheelIcon } from "./command_wheel_icons"
import { PromptField } from "./prompt_field"
import { OverlayRect, overlayRect } from "./search_field"
import { UiButton } from "./ui_button"
import { getUiMetrics } from "./ui_metrics"
import { TextRenderer } from "../text/text_renderer"
import {
    activitySummary, clampPage, creationLabel, generateBlocker, generationSlots, isBusy, jobOwner, jobStatus, jobTitle,
    pageCount,
} from "./spritelab_panel_model"

// The SpriteLab room's control panel: describe a creature or a piece of equipment, send it to the
// Lab, watch it move through the server's generation and publication queue, and spawn copies of
// anything already made.
//
// It replaced a DOM widget (a grey web form floating over the canvas). The panel is now the HUD's
// own furniture -- cut timber with a bronze rim (build_board.ts), sockets carved into it, glyphs from
// the command wheel -- and only the description box is still a DOM element, because a real text
// field is the one thing pixi cannot give a phone (see PromptField).
//
// Two states: EXPANDED, and FOLDED to its header, which keeps a spinner and a one-line summary so a
// minimized panel still says when something is cooking. The header is the grab handle in both.
//
// The owner (GuiManager) owns screen geometry: it clamps every drag, remembers where the panel was
// left and whether it was folded, and re-clamps when the panel changes height.

export interface SpriteLabPanelHandlers {
    onRequest: (request: SpriteLabRequest) => void
    // The press landed on the header, before any movement.
    onGrab: () => void
    // Where the pointer is asking the panel to go, in its parent's coordinates.
    onDragTo: (x: number, y: number) => void
    onRelease: () => void
    // The panel folded, unfolded or changed height.
    onLayoutChange: () => void
    // Any press on the panel: the owner cancels a held attack.
    onClaimInput: () => void
    // The description box took (true) or gave back (false) the keyboard.
    suppressGameKeys: (suppressed: boolean) => void
    // Page geometry for the DOM field: the canvas's client rect and the renderer's screen.
    canvasRect: () => OverlayRect | null
    rendererScreen: () => { width: number; height: number }
}

// ---- geometry (logical HUD units) ---------------------------------------------------------------
export const PANEL_W = 244
const BAND = 3 // the timber band around the face
const CHAMFER = 7
const PAD = 11 // face content inset from the panel edge
const INNER_W = PANEL_W - PAD * 2
const HEADER_H = 24
const KIND_H = 18
const PROMPT_H = 42
const ACTION_H = 20
const GENERATE_W = 82
const SECTION_H = 12
const ROW_H = 18
const MAX_QUEUE_ROWS = 5
const CREATION_ROWS = 3
const CREATION_ROW_H = 20
const SPAWN_W = 44
const LEAVE_W = 64
const FOOTER_H = 16
const GAP = 6

// ---- colours: build_board's, plus the two outcomes ---------------------------------------------
const HEADER_FACE = 0x241a11
const TITLE = 0xf3d9a4
const LABEL = 0xd9b483
const INK_DIM = 0xcbb49a
const PUBLISH = 0xf28c4e
const LIVE = 0x9bd47e
const FAILED = 0xe8745a
const MINE_FACE = 0x2a1f12

const PROMPT_MAX = 4000

// "paw" is drawn here; the wheel's set has no creature that is not a person.
type PanelIcon = WheelIcon | "paw"
const KIND_ICON: Record<SpriteLabMakeKind, PanelIcon> = { creature: "paw", equipment: "shield" }
const ENTRY_ICON: Record<SpriteLabEntry["kind"], PanelIcon> = { creature: "paw", item: "sack", entity: "tent" }

const label = (value: string, size: number, color: number, bold = false): Text =>
    new TextRenderer().fontSize(size).bold(bold).color(color).strokeThickness(0).render(value) as Text

const caps = (value: string, size: number, color: number, spacing = 1.4): Text => {
    const t = label(value.toUpperCase(), size, color, true)
    t.style.letterSpacing = spacing
    return t
}

const drawPaw = (g: Graphics) => {
    g.lineStyle(0).beginFill(0xffffff, 1)
    g.drawEllipse(0, 4.5, 6.2, 5)
    for (const [x, y] of [[-7.4, -2.6], [-2.8, -7.6], [2.8, -7.6], [7.4, -2.6]]) g.drawCircle(x, y, 2.9)
    g.endFill()
}

const glyph = (icon: PanelIcon, size: number, color: number): Graphics => {
    const g = new Graphics()
    if (icon === "paw") drawPaw(g)
    else drawWheelIcon(g, icon)
    g.scale.set(size / WHEEL_ICON_BOX)
    g.tint = color
    return g
}

// pixi adds the letter spacing after the last glyph too; centring by width alone sits a word right.
const inkWidth = (t: Text): number => Math.max(0, t.width - (Number(t.style.letterSpacing) || 0))

/** Set `value`, cut with an ellipsis to fit `max` measured pixels. Cosmetic: never allowed to throw. */
const fitText = (t: Text, value: string, max: number) => {
    t.text = value
    try {
        if (!(max > 0) || t.width <= max) return
        let lo = 0
        let hi = value.length
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            t.text = value.slice(0, mid).trimEnd() + "…"
            if (t.width <= max) lo = mid
            else hi = mid - 1
        }
        t.text = value.slice(0, lo).trimEnd() + "…"
    } catch {
        t.text = value
    }
}

/** A small carved-plate control drawn by hand: a toggle segment, a tab, a pager arrow. */
class Plate extends Container {
    private face = new Graphics()
    private hovered = false
    private enabled = true
    selected = false

    constructor(private w: number, private h: number, private onPress: () => void, private c = 3) {
        super()
        this.addChild(this.face)
        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = { contains: (x: number, y: number) => x >= -2 && y >= -2 && x <= this.w + 2 && y <= this.h + 2 } as any
        this.on("pointerover", () => { this.hovered = true; this.draw() })
        this.on("pointerout", () => { this.hovered = false; this.draw() })
        this.on("pointerdown", (event: InteractionEvent) => event.stopPropagation())
        this.on("pointertap", (event: InteractionEvent) => {
            event.stopPropagation()
            if (this.enabled) this.onPress()
        })
        this.draw()
    }

    setSelected(selected: boolean) {
        if (selected === this.selected) return
        this.selected = selected
        this.draw()
    }

    setEnabled(enabled: boolean) {
        if (enabled === this.enabled) return
        this.enabled = enabled
        this.cursor = enabled ? "pointer" : "default"
        this.alpha = enabled ? 1 : 0.4
        this.draw()
    }

    press = () => { if (this.enabled) this.onPress() }

    private draw() {
        const g = this.face
        const lit = this.selected
        g.clear()
        if (lit) {
            g.lineStyle({ width: 3, color: GOLD, alpha: 0.12, join: LINE_JOIN.MITER })
            chamfer(g, 0, 0, this.w, this.h, this.c)
        }
        g.lineStyle({ width: 1, color: lit ? GOLD : this.hovered && this.enabled ? RIM : PLOT_RIM, alpha: lit ? 0.95 : 0.85,
            join: LINE_JOIN.MITER })
        g.beginFill(lit ? PLOT_FACE_ON : this.hovered && this.enabled ? 0x1c160f : PLOT_FACE, lit ? 0.98 : 0.9)
        chamfer(g, 0.5, 0.5, this.w - 1, this.h - 1, this.c)
        g.endFill()
        g.lineStyle(0)
    }
}

interface QueueRow {
    job: SpriteLabJob
    container: Container
    icon: Graphics
    status: Text
    statusText: string
}

interface CreationRow {
    container: Container
    button: UiButton
}

export class SpriteLabPanel extends Container {
    private plate = new Graphics()
    private header = new Graphics()
    private spark: Graphics
    private title: Text
    private activity: Text
    private headerSpinner = new Graphics()
    private foldButton: Plate
    private foldGlyph = new Graphics()

    private body = new Container()
    private kindPlates: Record<SpriteLabMakeKind, Plate>
    private promptSocket = new Graphics()
    private hint: Text
    private generateButton: UiButton
    private queueTitle: Text
    private queueRule = new Graphics()
    private slotPips = new Graphics()
    private slotsLabel: Text
    private queueLayer = new Container()
    private queueRows: QueueRow[] = []
    private queueEmpty: Text
    private queueOverflow: Text
    private creationsHeader: Plate
    private creationsTitle: Text
    private creationsCount: Text
    private creationsChevron = new Graphics()
    private pagePrev: Plate
    private pageNext: Plate
    private pageLabel: Text
    private creationsLayer = new Container()
    private creationRows: CreationRow[] = []
    private creationsEmpty: Text
    private message: Text
    private leaveButton: UiButton

    private field: PromptField
    private state: SpriteLabState | null = null
    private stateAgeMs = 0
    private kind: SpriteLabMakeKind = "creature"
    private prompt = ""
    // A make is on the wire: hold Generate off until a reply says whether it was taken. A plain status
    // poll answered in the meantime says nothing either way, so it does not count; the clock is the
    // fallback for a reply that never comes.
    private pendingMake = false
    private pendingMs = 0
    private minimized = false
    private creationsOpen = false
    private page = 0
    private queueKey = ""
    private creationsKey = ""
    private messageMs = 0
    private panelHeight = 0
    private clockTick = 0
    private spin = 0
    private fieldCheckMs = 0
    private fieldFocused = false

    private grabbed = false
    private grabDx = 0
    private grabDy = 0
    private grabX = 0
    private grabY = 0
    private dragged = false

    constructor(private handlers: SpriteLabPanelHandlers) {
        super()
        this.plate.interactive = true
        this.plate.on("pointerdown", () => this.handlers.onClaimInput())
        this.addChild(this.plate, this.header)

        this.spark = glyph("spark", 13, GOLD)
        this.title = caps("Sprite Lab", 10, TITLE, 1.6)
        this.activity = label("", 9, INK_DIM)
        this.foldButton = new Plate(18, 16, () => this.toggleMinimized())
        this.foldButton.addChild(this.foldGlyph)
        this.addChild(this.spark, this.title, this.headerSpinner, this.activity, this.foldButton)

        this.header.interactive = true
        this.header.cursor = "grab"
        this.header.on("pointerdown", this.onGrab)
        this.interactive = true
        this.on("pointermove", this.onDragMove)
        this.on("pointerup", this.onRelease)
        this.on("pointerupoutside", this.onRelease)

        const kindW = (INNER_W - 4) / 2
        this.kindPlates = {
            creature: this.kindPlate("creature", "Creature", kindW),
            equipment: this.kindPlate("equipment", "Equipment", kindW),
        }
        this.kindPlates.equipment.x = kindW + 4
        const kinds = new Container()
        kinds.addChild(this.kindPlates.creature, this.kindPlates.equipment)
        kinds.name = "kinds"

        this.hint = new TextRenderer().fontSize(8).color(MUTED).strokeThickness(0).wordWrap(INNER_W - GENERATE_W - 8)
            .render("") as Text
        this.generateButton = new UiButton({ label: "Generate", onClick: () => this.generate(), theme: "ember",
            shape: "chamfer", tone: "accent", accent: GOLD, size: "small", width: GENERATE_W, height: ACTION_H,
            fontSize: 10, bold: true, hitPadding: 2 })

        this.queueTitle = caps("Queue", 8, LABEL)
        this.slotsLabel = label("slots", 8, MUTED)
        this.queueEmpty = label("Nothing cooking. Describe something above.", 8, MUTED)
        this.queueOverflow = label("", 8, MUTED)

        this.creationsHeader = new Plate(INNER_W, 16, () => this.setCreationsOpen(!this.creationsOpen))
        this.creationsTitle = caps("Creations", 8, LABEL)
        this.creationsCount = label("", 8, MUTED)
        this.creationsHeader.addChild(this.creationsChevron, this.creationsTitle, this.creationsCount)
        this.pagePrev = new Plate(14, 12, () => this.turnPage(-1), 2)
        this.pageNext = new Plate(14, 12, () => this.turnPage(1), 2)
        this.pagePrev.addChild(this.arrow(-1))
        this.pageNext.addChild(this.arrow(1))
        this.pageLabel = label("", 8, INK_DIM)
        this.creationsEmpty = label("Nothing made yet. Published creations appear here.", 8, MUTED)

        this.message = new TextRenderer().fontSize(8).color(INK_DIM).strokeThickness(0)
            .wordWrap(INNER_W - LEAVE_W - 8).render("") as Text
        this.leaveButton = new UiButton({ label: "Leave lab", onClick: () => this.leave(), theme: "ember",
            shape: "chamfer", appearance: "outline", size: "extraSmall", width: LEAVE_W, height: FOOTER_H, fontSize: 8,
            textColor: INK_DIM, hitPadding: 2 })

        this.body.addChild(kinds, this.promptSocket, this.hint, this.generateButton, this.queueRule, this.queueTitle,
            this.slotsLabel, this.slotPips, this.queueLayer, this.queueEmpty, this.queueOverflow, this.creationsHeader, this.pagePrev,
            this.pageLabel, this.pageNext, this.creationsLayer, this.creationsEmpty, this.message, this.leaveButton)
        this.addChild(this.body)

        this.field = new PromptField({
            placeholder: "A tiny blue dragon with golden horns…",
            ariaLabel: "Describe what SpriteLab should make",
            maxLength: PROMPT_MAX,
            onInput: value => { this.prompt = value; this.refreshAction() },
            onSubmit: () => this.generate(),
            onClaimInput: () => this.handlers.onClaimInput(),
            suppressGameKeys: suppressed => this.handlers.suppressGameKeys(suppressed),
        })

        this.visible = false
        this.selectKind("creature")
        this.layout()
    }

    // ---- public surface -------------------------------------------------------------------------

    update = (state: SpriteLabState | null, message?: string) => {
        const roomChanged = this.state?.roomId !== state?.roomId
        this.state = state
        this.stateAgeMs = 0
        this.visible = !!state
        if (!state) {
            this.pendingMake = false
            this.field.hide()
            this.setMessage("")
            return
        }
        if (roomChanged) this.setMessage("")
        if (message) this.setMessage(message)
        if (this.pendingMake && (state.ownPhase || message)) {
            this.pendingMake = false
            // Taken: the job exists now, so the description has done its work. Refused: keep it for a retry.
            if (state.ownPhase) this.setPrompt("")
        }
        this.refreshHeader()
        this.refreshQueue()
        this.refreshCreations()
        this.refreshAction()
        this.layout()
    }

    animate = (dtMs: number) => {
        if (!this.visible || !this.state) return
        const dt = Math.max(0, Math.min(250, Number.isFinite(dtMs) ? dtMs : 0))
        this.stateAgeMs += dt
        this.spin = (this.spin + dt * 0.0062) % (Math.PI * 2)
        this.headerSpinner.rotation = this.spin
        for (const row of this.queueRows) {
            if (row.job.phase === "generating") row.icon.rotation = this.spin
            else if (row.job.phase === "publishing") row.icon.rotation = this.spin * 1.6
            else if (row.job.phase === "queued") row.icon.alpha = 0.55 + 0.45 * (0.5 + 0.5 * Math.sin(this.spin * 2))
        }
        // The clocks move once a second; re-rasterising text every frame would be waste.
        this.clockTick += dt
        if (this.clockTick >= 500) {
            this.clockTick = 0
            this.refreshClocks()
        }
        if (this.messageMs > 0) {
            this.messageMs -= dt
            this.message.alpha = Math.max(0, Math.min(1, this.messageMs / 1500))
            if (this.messageMs <= 0) this.setMessage("")
        }
        if (this.pendingMake) {
            this.pendingMs -= dt
            if (this.pendingMs <= 0) {
                this.pendingMake = false
                this.refreshAction()
            }
        }
        this.fieldCheckMs -= dt
        if (this.fieldCheckMs <= 0) {
            this.fieldCheckMs = 200
            this.placeField()
        }
    }

    isMinimized = () => this.minimized
    isCreationsOpen = () => this.creationsOpen
    getWidth = (): number => PANEL_W * this.scale.x
    getHeight = (): number => this.panelHeight * this.scale.y

    setMinimized = (minimized: boolean) => {
        if (minimized === this.minimized) return
        this.minimized = minimized
        if (minimized) this.field.blur()
        this.refreshHeader()
        this.layout()
    }

    toggleMinimized = () => {
        this.setMinimized(!this.minimized)
        this.handlers.onLayoutChange()
    }

    setCreationsOpen = (open: boolean) => {
        if (open === this.creationsOpen) return
        this.creationsOpen = open
        this.refreshCreations()
        this.layout()
        this.handlers.onLayoutChange()
    }

    selectKind = (kind: SpriteLabMakeKind) => {
        this.kind = kind
        this.kindPlates.creature.setSelected(kind === "creature")
        this.kindPlates.equipment.setSelected(kind === "equipment")
    }

    getKind = () => this.kind

    /** Type into the description, as the DOM field would. */
    setPrompt = (value: string) => {
        this.field.setValue(value)
        this.prompt = value
        this.refreshAction()
    }

    generate = () => {
        const prompt = this.prompt.trim()
        if (generateBlocker(this.state, prompt, this.pendingMake)) return
        this.pendingMake = true
        this.pendingMs = 10000
        this.handlers.onClaimInput()
        this.handlers.onRequest({ action: "make", kind: this.kind, prompt })
        this.refreshAction()
    }

    spawn = (entry: SpriteLabEntry) => {
        this.handlers.onClaimInput()
        this.handlers.onRequest({ action: "spawn", kind: entry.kind, id: entry.id })
    }

    leave = () => {
        this.handlers.onClaimInput()
        this.field.blur()
        this.handlers.onRequest({ action: "leave" })
    }

    isGenerateEnabled = (): boolean => !generateBlocker(this.state, this.prompt, this.pendingMake)
    getHint = (): string => this.hint.text
    getActivity = (): string => this.activity.text
    getMessage = (): string => this.message.text
    /** Each queue row as "title · owner — status", for a caller outside the display tree. */
    getQueueLines = (): string[] => this.queueRows.map(row => {
        const owner = jobOwner(row.job)
        return `${jobTitle(row.job)}${owner ? ` · ${owner}` : ""} — ${row.statusText}`
    })
    getVisibleCreations = (): SpriteLabEntry[] => this.creationsOpen ? this.pageEntries() : []

    /** Keep the panel legible on a phone without letting it swallow the screen. */
    fitToViewport = () => {
        const metrics = getUiMetrics()
        const scale = metrics.touch ? Math.max(1, Math.min(1.6, 1.6 / Math.min(metrics.scaleX, metrics.scaleY),
            (metrics.safeViewport.width - 16) / PANEL_W)) : 1
        if (scale !== this.scale.x) {
            this.scale.set(scale)
            this.handlers.onLayoutChange()
        }
    }

    dispose = () => {
        this.field.dispose()
    }

    // ---- dragging -------------------------------------------------------------------------------

    private onGrab = (event: InteractionEvent) => {
        if (!this.parent) return
        this.handlers.onClaimInput()
        const pointer = event.data.getLocalPosition(this.parent)
        this.grabDx = this.x - pointer.x
        this.grabDy = this.y - pointer.y
        this.grabX = pointer.x
        this.grabY = pointer.y
        this.grabbed = true
        this.dragged = false
        this.header.cursor = "grabbing"
        this.handlers.onGrab()
    }

    private onDragMove = (event: InteractionEvent) => {
        if (!this.grabbed || !this.parent) return
        const pointer = event.data.getLocalPosition(this.parent)
        // A press that wanders a pixel or two is still a tap.
        if (!this.dragged && Math.hypot(pointer.x - this.grabX, pointer.y - this.grabY) < 4) return
        this.dragged = true
        this.handlers.onDragTo(pointer.x + this.grabDx, pointer.y + this.grabDy)
        this.fieldCheckMs = 0
    }

    private onRelease = () => {
        if (!this.grabbed) return
        this.grabbed = false
        this.header.cursor = "grab"
        // Tapping a folded header opens it: on a phone the header is a far bigger target than the button.
        if (!this.dragged && this.minimized) this.toggleMinimized()
        this.handlers.onRelease()
    }

    // ---- content --------------------------------------------------------------------------------

    private kindPlate(kind: SpriteLabMakeKind, name: string, w: number): Plate {
        const plate = new Plate(w, KIND_H, () => this.selectKind(kind))
        const icon = glyph(KIND_ICON[kind], 10, INK)
        const text = caps(name, 8, INK, 1.2)
        const total = 10 + 5 + inkWidth(text)
        icon.position.set((w - total) / 2 + 5, KIND_H / 2)
        text.position.set((w - total) / 2 + 15, Math.round((KIND_H - text.height) / 2))
        plate.addChild(icon, text)
        return plate
    }

    private arrow(direction: -1 | 1): Graphics {
        const g = new Graphics()
        g.lineStyle({ width: 1.5, color: INK, alpha: 0.9, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
        g.moveTo(7 - direction * 1.5, 3).lineTo(7 + direction * 1.5, 6).lineTo(7 - direction * 1.5, 9)
        return g
    }

    private setMessage(text: string) {
        this.message.text = text
        this.message.alpha = 1
        // Long enough to read a failure twice; the queue row carries the lasting state.
        this.messageMs = text ? 20000 : 0
        if (!text) this.layout()
    }

    private refreshHeader() {
        const state = this.state
        const busy = isBusy(state)
        this.headerSpinner.visible = busy
        // Expanded, the queue below says it all; the spinner alone marks that something is cooking.
        const text = state && this.minimized ? activitySummary(state, this.stateAgeMs) : ""
        if (this.activity.text !== text) this.activity.text = text
        this.drawFoldGlyph()
    }

    private refreshClocks() {
        for (const row of this.queueRows) {
            const status = jobStatus(row.job, this.stateAgeMs)
            if (status !== row.statusText) {
                row.statusText = status
                row.status.text = status
                row.status.x = INNER_W - 4 - row.status.width
            }
        }
        if (this.minimized) {
            const before = this.activity.text
            this.refreshHeader()
            if (before !== this.activity.text) this.layoutHeader()
        }
    }

    private refreshAction() {
        const blocker = generateBlocker(this.state, this.prompt, this.pendingMake)
        this.generateButton.setEnabled(!blocker)
        const hint = blocker && blocker !== "Describe what to make" ? blocker
            : getUiMetrics().touch ? "Tap Generate when ready" : "Press Enter to generate"
        if (this.hint.text !== hint) this.hint.text = hint
        this.hint.tint = blocker && blocker !== "Describe what to make" ? 0xe9b26a : MUTED
    }

    private refreshQueue() {
        const jobs = this.state?.jobs ?? []
        const key = JSON.stringify(jobs.map(job => [job.id, job.phase, job.draftId, job.queuePosition, job.mine, job.player]))
        if (key === this.queueKey) {
            // Same rows; only the clocks moved. Re-seat them on the server's measurement.
            this.queueRows.forEach(row => { row.job = jobs.find(job => job.id === row.job.id) || row.job })
            this.refreshClocks()
            return
        }
        this.queueKey = key
        for (const row of this.queueRows) row.container.destroy({ children: true, texture: true, baseTexture: true })
        this.queueRows = []
        const shown = jobs.slice(0, MAX_QUEUE_ROWS)
        shown.forEach((job, index) => {
            const row = this.buildQueueRow(job)
            row.container.y = index * ROW_H
            this.queueLayer.addChild(row.container)
            this.queueRows.push(row)
        })
        this.queueEmpty.visible = shown.length === 0
        const overflow = jobs.length - shown.length
        this.queueOverflow.text = overflow > 0 ? `+${overflow} more` : ""
        this.queueOverflow.visible = overflow > 0
        this.drawSlotPips()
    }

    private buildQueueRow(job: SpriteLabJob): QueueRow {
        const container = new Container()
        const back = new Graphics()
        if (job.mine) {
            back.lineStyle({ width: 1, color: GOLD, alpha: 0.28, join: LINE_JOIN.MITER })
            back.beginFill(MINE_FACE, 0.9)
            chamfer(back, -3, 1, INNER_W + 6, ROW_H - 2, 3)
            back.endFill()
        }
        const icon = this.jobIcon(job)
        icon.position.set(5, ROW_H / 2)
        const status = label(jobStatus(job, this.stateAgeMs), 8, job.phase === "failed" ? FAILED
            : job.phase === "done" ? LIVE : job.phase === "publishing" ? PUBLISH : job.mine ? GOLD : INK_DIM, job.mine)
        status.x = INNER_W - 4 - status.width
        status.y = Math.round((ROW_H - status.height) / 2)
        const owner = jobOwner(job)
        const ownerText = label(owner ? `· ${owner}` : "", 8, MUTED)
        const title = label("", 9, job.mine ? INK : INK_DIM, job.mine)
        const titleMax = INNER_W - 18 - status.width - 10 - (owner ? Math.min(60, ownerText.width) + 4 : 0)
        fitText(title, jobTitle(job), titleMax)
        title.position.set(14, Math.round((ROW_H - title.height) / 2))
        fitText(ownerText, owner ? `· ${owner}` : "", 60)
        ownerText.position.set(title.x + title.width + 4, Math.round((ROW_H - ownerText.height) / 2) + 0.5)
        ownerText.visible = !!owner
        container.addChild(back, icon, title, ownerText, status)
        return { job, container, icon, status, statusText: status.text }
    }

    /** A spinner while the Lab is drawing or the server publishing, a breathing ring in the queue, then the outcome. */
    private jobIcon(job: SpriteLabJob): Graphics {
        const g = new Graphics()
        const r = 4.6
        if (job.phase === "done") {
            g.lineStyle({ width: 1.8, color: LIVE, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
            g.moveTo(-3.4, 0.2).lineTo(-1, 2.8).lineTo(3.6, -2.6)
            return g
        }
        if (job.phase === "failed") {
            g.lineStyle({ width: 1.8, color: FAILED, alpha: 1, cap: LINE_CAP.ROUND })
            g.moveTo(-2.8, -2.8).lineTo(2.8, 2.8).moveTo(2.8, -2.8).lineTo(-2.8, 2.8)
            return g
        }
        if (job.phase === "queued") {
            g.lineStyle({ width: 1.2, color: RIM_LIGHT, alpha: 0.75 })
            g.drawCircle(0, 0, r - 0.6)
            g.lineStyle(0).beginFill(RIM_LIGHT, 0.9).drawCircle(0, 0, 1.4).endFill()
            return g
        }
        this.drawSpinner(g, r, job.phase === "publishing" ? PUBLISH : GOLD)
        return g
    }

    private drawSpinner(g: Graphics, r: number, color: number) {
        g.clear()
        g.lineStyle({ width: 1.6, color, alpha: 0.18 })
        g.drawCircle(0, 0, r)
        g.lineStyle({ width: 1.8, color, alpha: 1, cap: LINE_CAP.ROUND })
        g.moveTo(r, 0)
        g.arc(0, 0, r, 0, Math.PI * 1.35)
    }

    private drawSlotPips() {
        const g = this.slotPips
        g.clear()
        const state = this.state
        if (!state) return
        const slots = generationSlots(state)
        for (let i = 0; i < slots; i++) {
            const used = i < state.generating
            const x = -i * 9
            g.lineStyle({ width: 1, color: used ? GOLD : PLOT_RIM, alpha: 1 })
            g.beginFill(used ? GOLD : PLOT_FACE, used ? 0.95 : 1)
            g.drawCircle(x, 0, 3)
            g.endFill()
        }
        g.lineStyle(0)
    }

    private entries(): SpriteLabEntry[] {
        return this.state?.entries ?? []
    }

    private pageEntries(): SpriteLabEntry[] {
        const entries = this.entries()
        this.page = clampPage(this.page, entries.length, CREATION_ROWS)
        return entries.slice(this.page * CREATION_ROWS, this.page * CREATION_ROWS + CREATION_ROWS)
    }

    private turnPage(step: number) {
        const total = this.entries().length
        const next = clampPage(this.page + step, total, CREATION_ROWS)
        if (next === this.page) return
        this.page = next
        this.creationsKey = ""
        this.refreshCreations()
    }

    private refreshCreations() {
        const entries = this.entries()
        this.creationsCount.text = String(entries.length)
        const pages = pageCount(entries.length, CREATION_ROWS)
        const visible = this.creationsOpen ? this.pageEntries() : []
        const key = JSON.stringify([this.creationsOpen, this.page, visible])
        this.pageLabel.text = `${this.page + 1} / ${pages}`
        this.pagePrev.setEnabled(this.page > 0)
        this.pageNext.setEnabled(this.page < pages - 1)
        this.drawChevron()
        if (key === this.creationsKey) return
        this.creationsKey = key
        for (const row of this.creationRows) {
            row.button.releaseMouseControl()
            row.container.destroy({ children: true, texture: true, baseTexture: true })
        }
        this.creationRows = []
        visible.forEach((entry, index) => {
            const container = new Container()
            const icon = glyph(ENTRY_ICON[entry.kind], 10, LABEL)
            icon.position.set(5, CREATION_ROW_H / 2)
            const kindText = label(entry.kind === "entity" ? "prop" : entry.kind, 8, MUTED)
            const name = label("", 9, INK)
            fitText(name, creationLabel(entry.id), INNER_W - 16 - SPAWN_W - 8 - kindText.width - 6)
            name.position.set(14, Math.round((CREATION_ROW_H - name.height) / 2))
            kindText.position.set(name.x + name.width + 5, Math.round((CREATION_ROW_H - kindText.height) / 2) + 0.5)
            const button = new UiButton({ label: "Spawn", onClick: () => this.spawn(entry), theme: "ember", shape: "chamfer",
                size: "extraSmall", width: SPAWN_W, height: 15, fontSize: 8, textColor: INK, hitPadding: 2 })
            button.position.set(INNER_W - SPAWN_W, Math.round((CREATION_ROW_H - 15) / 2))
            const rule = new Graphics()
            if (index > 0) groove(rule, 0, INNER_W, 0)
            container.addChild(rule, icon, name, kindText, button)
            container.y = index * CREATION_ROW_H
            this.creationsLayer.addChild(container)
            this.creationRows.push({ container, button })
        })
    }

    private drawChevron() {
        const g = this.creationsChevron
        g.clear()
        g.lineStyle({ width: 1.4, color: LABEL, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
        if (this.creationsOpen) g.moveTo(-3, -1.5).lineTo(0, 1.5).lineTo(3, -1.5)
        else g.moveTo(-1.5, -3).lineTo(1.5, 0).lineTo(-1.5, 3)
        g.position.set(9, 8)
    }

    private drawFoldGlyph() {
        const g = this.foldGlyph
        g.clear()
        g.lineStyle({ width: 1.6, color: INK, alpha: 0.95, cap: LINE_CAP.ROUND })
        g.moveTo(5, 8).lineTo(13, 8)
        if (this.minimized) g.moveTo(9, 4).lineTo(9, 12)
    }

    // ---- layout ---------------------------------------------------------------------------------

    private layoutHeader() {
        const cy = BAND + HEADER_H / 2
        this.spark.position.set(PAD + 5, cy)
        this.title.position.set(PAD + 15, Math.round(cy - this.title.height / 2))
        this.foldButton.position.set(PANEL_W - PAD - 16, Math.round(cy - 8))
        const right = this.foldButton.x - 6
        const titleEnd = this.title.x + inkWidth(this.title) + 6
        fitText(this.activity, this.activity.text, Math.max(0, right - titleEnd - 12))
        this.activity.position.set(right - this.activity.width, Math.round(cy - this.activity.height / 2))
        this.headerSpinner.clear()
        this.drawSpinner(this.headerSpinner, 4.2, GOLD)
        this.headerSpinner.position.set(this.activity.x - (this.activity.text ? 9 : 4), cy)
    }

    private layout() {
        this.layoutHeader()
        this.body.visible = !this.minimized
        let y = BAND + HEADER_H + 7
        if (!this.minimized) {
            const kinds = this.body.getChildByName("kinds")
            kinds.position.set(PAD, y)
            y += KIND_H + 5

            this.promptSocket.position.set(PAD, y)
            this.drawPromptSocket()
            y += PROMPT_H + 5

            this.generateButton.position.set(PAD + INNER_W - GENERATE_W, y)
            this.hint.position.set(PAD, y + Math.max(0, Math.round((ACTION_H - this.hint.height) / 2)))
            y += ACTION_H + GAP + 2

            // Queue: a section label with the generation slots on the right, then one row per job.
            this.queueTitle.position.set(PAD, y)
            this.queueRule.clear()
            const pipsW = this.state ? generationSlots(this.state) * 9 : 18
            this.slotsLabel.position.set(PAD + INNER_W - pipsW - 3 - this.slotsLabel.width, y + Math.round((SECTION_H - this.slotsLabel.height) / 2) - 1)
            groove(this.queueRule, PAD + inkWidth(this.queueTitle) + 6, this.slotsLabel.x - 6, y + SECTION_H / 2 - 1)
            this.slotPips.position.set(PAD + INNER_W - 3, y + SECTION_H / 2 - 1)
            y += SECTION_H + 2
            this.queueLayer.position.set(PAD, y)
            const rows = Math.max(1, this.queueRows.length)
            this.queueEmpty.position.set(PAD + 2, y + Math.round((ROW_H - this.queueEmpty.height) / 2))
            y += rows * ROW_H
            if (this.queueOverflow.visible) {
                this.queueOverflow.position.set(PAD + 14, y)
                y += 11
            }
            y += GAP

            // Creations: a fold-out list with its own pager.
            this.creationsHeader.position.set(PAD, y)
            this.creationsTitle.position.set(18, Math.round((16 - this.creationsTitle.height) / 2))
            this.creationsCount.position.set(this.creationsTitle.x + inkWidth(this.creationsTitle) + 6,
                Math.round((16 - this.creationsCount.height) / 2))
            const showPager = this.creationsOpen && this.entries().length > CREATION_ROWS
            for (const part of [this.pagePrev, this.pageLabel, this.pageNext]) part.visible = showPager
            this.pageNext.position.set(PAD + INNER_W - 18, y + 2)
            this.pageLabel.position.set(this.pageNext.x - 4 - this.pageLabel.width, y + Math.round((16 - this.pageLabel.height) / 2))
            this.pagePrev.position.set(this.pageLabel.x - 4 - 14, y + 2)
            y += 16
            this.creationsLayer.visible = this.creationsOpen
            this.creationsEmpty.visible = this.creationsOpen && this.entries().length === 0
            if (this.creationsOpen) {
                y += 3
                this.creationsLayer.position.set(PAD, y)
                this.creationsEmpty.position.set(PAD + 2, y + 4)
                y += Math.max(1, this.creationRows.length) * CREATION_ROW_H
            }
            y += GAP

            // Footer: the latest word from the server, and the way out.
            const messageH = this.message.text ? this.message.height : 0
            const footerH = Math.max(FOOTER_H, messageH)
            this.message.position.set(PAD, y + Math.round((footerH - messageH) / 2))
            this.leaveButton.position.set(PAD + INNER_W - LEAVE_W, y + Math.round((footerH - FOOTER_H) / 2))
            y += footerH + PAD - 2
        } else {
            y = BAND + HEADER_H + BAND
        }
        const height = Math.round(y)
        if (height !== this.panelHeight) {
            this.panelHeight = height
            this.drawFrame()
            this.handlers.onLayoutChange()
        } else {
            this.drawFrame()
        }
        this.fieldCheckMs = 0
    }

    private drawFrame() {
        const g = this.plate
        const w = PANEL_W
        const h = this.panelHeight
        g.clear()
        // Dropped silhouette: the panel sits on the world rather than being painted into it.
        g.beginFill(0x000000, 0.34)
        chamfer(g, 0, 3, w, h, CHAMFER)
        g.endFill()
        g.lineStyle({ width: 1.5, color: TIMBER_EDGE, alpha: 0.95, join: LINE_JOIN.MITER })
        g.beginFill(TIMBER, 0.98)
        chamfer(g, 0, 0, w, h, CHAMFER)
        g.endFill()
        g.lineStyle({ width: 1, color: RIM, alpha: 0.85, join: LINE_JOIN.MITER })
        g.beginFill(FACE, 0.97)
        chamfer(g, BAND, BAND, w - BAND * 2, h - BAND * 2, CHAMFER - 2)
        g.endFill()
        g.lineStyle(0)

        // The header band: a little lighter (the fire catches it), cut off from the face by a groove.
        const hb = this.header
        hb.clear()
        hb.beginFill(HEADER_FACE, this.minimized ? 0.0 : 0.85)
        hb.drawRect(BAND + 1, BAND + 1, w - BAND * 2 - 2, HEADER_H - 1)
        hb.endFill()
        hb.hitArea = { contains: (x: number, y: number) => x >= 0 && x <= w && y >= 0 && y <= BAND + HEADER_H } as any
        if (!this.minimized) groove(hb, BAND + 2, w - BAND - 2, BAND + HEADER_H)
        hb.lineStyle(1, RIM_LIGHT, 0.16).moveTo(BAND + CHAMFER, BAND + 1.5).lineTo(w - BAND - CHAMFER, BAND + 1.5)
        hb.lineStyle(0)
    }

    private drawPromptSocket() {
        const g = this.promptSocket
        g.clear()
        const focused = this.fieldFocused
        g.lineStyle(0)
        g.beginFill(0x0a0705, 1)
        chamfer(g, 0, 0, INNER_W, PROMPT_H, 3)
        g.endFill()
        g.beginFill(0x120d09, 1)
        chamfer(g, 1.5, 2, INNER_W - 3, PROMPT_H - 3.5, 2)
        g.endFill()
        g.lineStyle(1, focused ? GOLD : RIM_LIGHT, focused ? 0.6 : 0.12)
        g.moveTo(3, PROMPT_H - 0.5).lineTo(INNER_W - 3, PROMPT_H - 0.5)
        g.lineStyle(0)
    }

    /** Park the DOM field over the socket, or take it off the page when the socket is not showing. */
    private placeField() {
        const showing = this.visible && !this.minimized && !!this.state && this.worldVisible
        if (!showing) {
            if (this.field.isShown()) this.field.hide()
            return
        }
        const canvas = this.handlers.canvasRect()
        const screen = this.handlers.rendererScreen()
        if (!canvas || !(screen.width > 0)) return
        const start = this.promptSocket.toGlobal({ x: 2, y: 2 })
        const end = this.promptSocket.toGlobal({ x: INNER_W - 2, y: PROMPT_H - 2 })
        this.field.show(overlayRect({ x: start.x, y: start.y, width: end.x - start.x, height: end.y - start.y }, screen, canvas),
            getUiMetrics().touch)
        // The socket's lower lip lights gold while the field has the keyboard.
        const focused = this.field.isFocused()
        if (focused !== this.fieldFocused) {
            this.fieldFocused = focused
            this.drawPromptSocket()
        }
    }
}
